import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Bell,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  TrendingDown,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import {
  getErrorMessage,
  notificationAPI,
  preferencesAPI,
} from '../../utils/api';

const NotificationsNew = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    priceAlerts: true,
    transactionUpdates: true,
    securityAlerts: true,
    news: false,
    marketing: false,
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        setLoading(true);
        const response = await notificationAPI.getAll();
        setNotifications(response.data || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load notifications'));
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await preferencesAPI.get();
        const prefs = response.data || {};
        setNotificationSettings({
          priceAlerts: prefs.price_alerts ?? true,
          transactionUpdates: prefs.transaction_updates ?? true,
          securityAlerts: prefs.security_alerts ?? true,
          news: prefs.news_updates ?? false,
          marketing: prefs.marketing_updates ?? false,
        });
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load settings'));
      }
    };

    loadSettings();
  }, []);

  const updateNotificationSettings = async (updates) => {
    const previous = notificationSettings;
    const next = { ...notificationSettings, ...updates };
    setNotificationSettings(next);
    try {
      setIsSavingSettings(true);
      await preferencesAPI.update({
        price_alerts: next.priceAlerts,
        transaction_updates: next.transactionUpdates,
        security_alerts: next.securityAlerts,
        news_updates: next.news,
        marketing_updates: next.marketing,
      });
    } catch (error) {
      setNotificationSettings(previous);
      toast.error(getErrorMessage(error, 'Failed to update settings'));
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId ? { ...item, is_read: true } : item
        )
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update notification'));
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter((item) => !item.is_read);
    if (unread.length === 0) {
      return;
    }
    try {
      await Promise.all(unread.map((item) => notificationAPI.markRead(item.id)));
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, is_read: true }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to mark notifications'));
    }
  };

  const formatTime = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'trade':
      case 'transaction':
        return { Icon: CheckCircle, iconBg: 'bg-green-500' };
      case 'deposit':
        return { Icon: TrendingUp, iconBg: 'bg-green-500' };
      case 'withdrawal':
        return { Icon: TrendingDown, iconBg: 'bg-red-500' };
      case 'ticket':
        return { Icon: AlertTriangle, iconBg: 'bg-orange-500' };
      case 'kyc':
        return { Icon: AlertTriangle, iconBg: 'bg-orange-500' };
      default:
        return { Icon: Bell, iconBg: 'bg-blue-500' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="notifications-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Notifications
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm md:text-base">
          You have {unreadCount} unread notifications
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Notifications List */}
        <div className="lg:col-span-8">
          <Card className="border-none shadow-none">
            <div className="flex items-center justify-between mb-6 p-5">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                All Notifications
              </h2>
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
              >
                Mark all as read
              </button>
            </div>

            <div className="">
              {notifications.map((notification) => {
                const { Icon, iconBg } = getIconForType(notification.type);
                return (
                  <div
                    key={notification.id}
                    onClick={() =>
                      !notification.is_read && handleMarkRead(notification.id)
                    }
                    className={`flex items-start space-x-4 p-5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-800 ${!notification.is_read
                      ? 'bg-[#062136]/5'
                      : 'bg-white dark:bg-gray-800'
                      }`}
                  >
                    {/* Icon with colored background */}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${iconBg} flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-[#062136] rounded-full mt-2 ml-2 flex-shrink-0"></div>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        {notification.message}
                      </p>
                      <p className="text-gray-500 dark:text-gray-500 text-xs mt-2">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Right Column - Settings */}
        <div className="lg:col-span-4 space-y-6">
          {/* Notification Settings Card */}
          {/* <Card className="p-6 border-none shadow-none">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
              <SettingsIcon className="w-5 h-5 mr-2" />
              Notification Settings
            </h2>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Price Alerts
                </span>
                <Switch
                  checked={notificationSettings.priceAlerts}
                  onCheckedChange={(checked) =>
                    updateNotificationSettings({ priceAlerts: checked })
                  }
                  disabled={isSavingSettings}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Transaction Updates
                </span>
                <Switch
                  checked={notificationSettings.transactionUpdates}
                  onCheckedChange={(checked) =>
                    updateNotificationSettings({ transactionUpdates: checked })
                  }
                  disabled={isSavingSettings}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Security Alerts
                </span>
                <Switch
                  checked={notificationSettings.securityAlerts}
                  onCheckedChange={(checked) =>
                    updateNotificationSettings({ securityAlerts: checked })
                  }
                  disabled={isSavingSettings}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  News & Updates
                </span>
                <Switch
                  checked={notificationSettings.news}
                  onCheckedChange={(checked) =>
                    updateNotificationSettings({ news: checked })
                  }
                  disabled={isSavingSettings}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Marketing
                </span>
                <Switch
                  checked={notificationSettings.marketing}
                  onCheckedChange={(checked) =>
                    updateNotificationSettings({ marketing: checked })
                  }
                  disabled={isSavingSettings}
                />
              </div>
            </div>
          </Card> */}

          {/* Set Price Alerts Card */}
          {/* <Card className="p-6 bg-[#062136] border-0">
            <h2 className="text-xl font-bold text-white mb-2">
              <span className="text-white">
                Set Price Alerts
                </span>
            </h2>
            <p className="text-white/90 text-sm mb-6">
              Get notified when your favorite coins hit your target price.
            </p>
            <Button
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold"
              onClick={() => {
                // Navigate to price alerts page or open modal
                console.log('Create alert clicked');
              }}
            >
              Create Alert
            </Button>
          </Card> */}
        </div>
      </div>
    </div>
  );
};

export default NotificationsNew;
